import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskLine } from "./motion";
import { TRAINER } from "../data";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="home" ref={ref} className="relative min-h-screen overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y }} className="absolute inset-0 md:left-auto md:right-0 md:w-[54%] z-0">
        <img src={TRAINER.heroImage} alt="Trainer" className="w-full h-[112%] object-cover object-top grayscale" />
        {/* darken image on mobile so text is readable over full-bleed */}
        <div className="absolute inset-0 bg-[#0A0A0A]/55 md:bg-transparent" />
        {/* bottom fade into page */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
        {/* left-edge fade so the figure melts into the dark text side on desktop */}
        <div className="absolute inset-0 md:bg-gradient-to-r md:from-[#0A0A0A] md:via-[#0A0A0A]/10 md:to-transparent" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 min-h-screen flex flex-col justify-end px-4 md:px-8 lg:px-12 pb-16 md:pb-24 md:max-w-[64%]">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mb-6 flex items-center gap-3"
        >
          <span className="w-8 h-px bg-[#FF3B30]" />
          <span className="text-xs uppercase tracking-[0.3em] text-zinc-300">{TRAINER.role} — {TRAINER.location}</span>
        </motion.div>

        <h1 className="font-display text-white text-6xl sm:text-7xl lg:text-[9.5vw] leading-[0.82] tracking-tight">
          {TRAINER.tagline.map((line, i) => (
            <MaskLine key={i} delay={0.3 + i * 0.12} className={i === TRAINER.tagline.length - 1 ? "text-[#FF3B30]" : ""}>
              {line}
            </MaskLine>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-6 max-w-2xl"
        >
          <p className="text-zinc-400 text-base lg:text-lg max-w-md">
            Elite personal training & coaching. Book a complimentary consultation.
          </p>
          <button
            onClick={() => document.getElementById("book")?.scrollIntoView({ behavior: "smooth" })}
            data-testid="hero-cta"
            className="shrink-0 bg-[#FF3B30] text-white px-8 py-4 font-bold uppercase tracking-wider hover:bg-[#D6281F] transition-colors"
          >
            Book Free Consult
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-8 right-4 md:right-12 z-10 flex items-center gap-2 text-zinc-500"
      >
        <span className="text-xs uppercase tracking-widest">Scroll</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.6 }} className="w-px h-8 bg-zinc-600 block" />
      </motion.div>
    </section>
  );
};
